import React, { useEffect, useState } from "react";
import api, { money } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import {
  BedDouble, LogIn, LogOut, CalendarCheck, Percent, Wallet, Banknote, CreditCard,
  UtensilsCrossed, Receipt, TrendingUp,
} from "lucide-react";

function Stat({ icon: Icon, label, value, accent = "text-forest" }) {
  return (
    <div className="bg-white rounded-2xl border border-gold/10 shadow-soft p-5" data-testid={`stat-${label}`}>
      <div className="flex items-center gap-3">
        <div className="w-11 h-11 rounded-xl bg-cream-muted flex items-center justify-center">
          <Icon className={`w-6 h-6 ${accent}`} />
        </div>
        <div>
          <div className="text-2xl font-bold text-forest-deep leading-none">{value}</div>
          <div className="text-xs text-muted-foreground mt-1">{label}</div>
        </div>
      </div>
    </div>
  );
}

export default function Dashboard() {
  const { user } = useAuth();
  const [d, setD] = useState(null);
  const isOwner = user.role === "OWNER";

  useEffect(() => {
    const load = () => api.get("/dashboard").then((r) => setD(r.data)).catch(() => {});
    load();
    const iv = setInterval(load, 8000);
    return () => clearInterval(iv);
  }, []);

  if (!d) return <div className="p-6 text-muted-foreground">Зареждане…</div>;

  return (
    <div className="p-6 space-y-8">
      <div>
        <h1 className="font-display text-4xl text-forest-deep">Табло</h1>
        <div className="gold-rule w-24 my-3" />
      </div>

      <div>
        <h2 className="text-sm uppercase tracking-wider text-gold-dark mb-3">Днес · Хотел</h2>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          <Stat icon={BedDouble} label="Заети стаи" value={`${d.hotel.occupied_rooms}/${d.hotel.total_rooms}`} />
          <Stat icon={BedDouble} label="Свободни стаи" value={d.hotel.available_rooms} accent="text-emerald-600" />
          <Stat icon={LogIn} label="Пристигания днес" value={d.hotel.arrivals_today} accent="text-blue-600" />
          <Stat icon={LogOut} label="Заминавания днес" value={d.hotel.departures_today} accent="text-amber-600" />
          <Stat icon={CalendarCheck} label="Резервации днес" value={d.hotel.reservations_today} />
          <Stat icon={Percent} label="Заетост" value={`${d.hotel.occupancy_rate}%`} accent="text-gold-dark" />
        </div>
        {isOwner && <div className="mt-4"><Stat icon={Wallet} label="Хотелски приход днес" value={money(d.hotel.revenue_today)} accent="text-forest" /></div>}
      </div>

      {isOwner && (
        <div>
          <h2 className="text-sm uppercase tracking-wider text-gold-dark mb-3">Днес · Ресторант</h2>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
            <Stat icon={TrendingUp} label="Оборот" value={money(d.restaurant.turnover)} accent="text-forest" />
            <Stat icon={Banknote} label="В брой" value={money(d.restaurant.cash)} accent="text-emerald-600" />
            <Stat icon={CreditCard} label="С карта" value={money(d.restaurant.card)} accent="text-blue-600" />
            <Stat icon={UtensilsCrossed} label="Отворени маси" value={d.restaurant.open_tables} accent="text-amber-600" />
            <Stat icon={Receipt} label="Затворени днес" value={d.restaurant.closed_tables} />
            <Stat icon={Receipt} label="Брой поръчки" value={d.restaurant.orders_count} />
          </div>
        </div>
      )}

      <div className="grid md:grid-cols-2 gap-6">
        <div className="bg-white rounded-2xl border border-gold/10 shadow-soft p-5">
          <h3 className="font-display text-2xl text-forest-deep">Предстоящи резервации</h3>
          <div className="gold-rule w-16 my-3" />
          <div className="space-y-2">
            {d.upcoming_reservations.length === 0 && <p className="text-muted-foreground text-sm">Няма</p>}
            {d.upcoming_reservations.map((r) => (
              <div key={r.id} className="flex justify-between items-center py-2 border-b border-gold/5">
                <div>
                  <div className="font-medium text-forest-deep">{r.first_name} {r.last_name}</div>
                  <div className="text-xs text-muted-foreground">Стая {r.room_number} · {r.arrival} → {r.departure}</div>
                </div>
                <span className="text-xs bg-cream-muted px-2 py-1 rounded-lg text-forest">{r.nights} нощ.</span>
              </div>
            ))}
          </div>
        </div>

        {isOwner && (
          <div className="bg-white rounded-2xl border border-gold/10 shadow-soft p-5">
            <h3 className="font-display text-2xl text-forest-deep">Последни поръчки</h3>
            <div className="gold-rule w-16 my-3" />
            <div className="space-y-2">
              {d.recent_orders.length === 0 && <p className="text-muted-foreground text-sm">Няма</p>}
              {d.recent_orders.map((o) => (
                <div key={o.id} className="flex justify-between items-center py-2 border-b border-gold/5">
                  <div>
                    <div className="font-medium text-forest-deep">Маса {o.table_number}</div>
                    <div className="text-xs text-muted-foreground">{o.waitress_name} · {o.payment?.method === "CASH" ? "В брой" : "Карта"}</div>
                  </div>
                  <span className="font-bold text-forest-deep">{money(o.total)}</span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    </div>
  );
}
